import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import AttendanceMarkerPopup from './AttendanceMarkerPopup';

// Fix Leaflet default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom colored markers using divIcon
const createRoleIcon = (role) => {
  const colors = {
    site_supervisor: '#22c55e', // green-500
    site_manager: '#3b82f6',    // blue-500
    sid: '#a855f7',             // purple-500
    asid: '#6366f1',            // indigo-500
    operation_head: '#f97316',  // orange-500
    default: '#6b7280'          // gray-500
  };

  const color = colors[role] || colors.default;

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 30px;
        height: 30px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

// Component to fit bounds when markers change
function MapBoundsUpdater({ checkIns }) {
  const map = useMap();

  useEffect(() => {
    if (checkIns.length > 0) {
      const bounds = L.latLngBounds(
        checkIns.map(c => [c.latitude, c.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [checkIns, map]);

  return null;
}

export default function AttendanceMap({ checkIns = [], height, isMobile = false, onMarkerClick }) {
  // Default center: Kuala Lumpur
  const defaultCenter = [3.139003, 101.686855];
  const defaultZoom = 11;

  // Memoize icons to avoid recreating them on every render
  const markerIcons = useMemo(() => {
    const icons = {};
    checkIns.forEach(checkIn => {
      if (!icons[checkIn.userRole]) {
        icons[checkIn.userRole] = createRoleIcon(checkIn.userRole);
      }
    });
    return icons;
  }, [checkIns]);

  // Empty state
  if (checkIns.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 h-[320px] sm:h-[450px]"
      >
        <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mb-2 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <p className="text-gray-500 text-base sm:text-lg font-medium">No Active Personnel</p>
        <p className="text-gray-400 text-xs sm:text-sm mt-1 text-center px-4">No one has checked in without checking out today</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
      {/* Map container with responsive height - taller on mobile to fit popups */}
      <div className="h-[320px] sm:h-[450px]" style={height ? { height } : undefined}>
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapBoundsUpdater checkIns={checkIns} />

          {checkIns.map(checkIn => (
            <Marker
              key={checkIn.id}
              position={[checkIn.latitude, checkIn.longitude]}
              icon={markerIcons[checkIn.userRole] || createRoleIcon('default')}
              eventHandlers={isMobile ? {
                click: () => onMarkerClick?.(checkIn)
              } : undefined}
            >
              {/* Only render Popup on desktop - mobile uses bottom sheet */}
              {!isMobile && (
                <Popup
                  maxWidth={300}
                  minWidth={240}
                  className="attendance-popup"
                  autoPan={true}
                  autoPanPadding={[20, 80]}
                  keepInView={true}
                >
                  <AttendanceMarkerPopup checkIn={checkIn} />
                </Popup>
              )}
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Legend - compact on mobile */}
      <div className="bg-white border-t border-gray-200 px-2 sm:px-4 py-1.5 sm:py-2 flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-4 text-xs">
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></span>
          <span className="text-gray-600"><span className="hidden sm:inline">Site </span>Supervisor</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500"></span>
          <span className="text-gray-600"><span className="hidden sm:inline">Site </span>Manager</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-purple-500"></span>
          <span className="text-gray-600">SID</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-indigo-500"></span>
          <span className="text-gray-600">ASID</span>
        </div>
      </div>
    </div>
  );
}
